import React, { useEffect, useState } from "react";
import api from "../../api";
import { Link } from "react-router-dom";

const GalleryIndex = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const pageSize = 12;

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return path.startsWith("/")
      ? `${process.env.REACT_APP_IMG_URL}${path}`
      : `${process.env.REACT_APP_IMG_URL}/${path}`;
  };

  const fetchGallery = async (page = 1, append = false, search = "") => {
    append ? setLoadingMore(true) : setLoading(true);
    setError("");

    try {
      const response = await api.get("/gallery", {
        params: { page, pageSize },
      });

      let newItems = Array.isArray(response.data) ? response.data : [];

      if (search) {
        const loweredSearch = search.toLowerCase();
        newItems = newItems.filter(
          (item) =>
            item.id?.toString().includes(loweredSearch) ||
            item.postId?.toString().includes(loweredSearch) ||
            item.postTitle?.toLowerCase().includes(loweredSearch) ||
            item.postSlug?.toLowerCase().includes(loweredSearch) ||
            item.category?.toLowerCase().includes(loweredSearch)
        );
      }

      setGalleryItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setCurrentPage(page);
      setHasMore(newItems.length === pageSize);
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement de la galerie.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchGallery(1, false, searchTerm);
  }, [searchTerm]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleResetSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  const handleLoadMore = () => {
    if (!hasMore || loadingMore) return;
    fetchGallery(currentPage + 1, true, searchTerm);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-slate-200">
        Chargement...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100">
      <div className="mx-auto max-w-7xl">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-violet-900 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-violet-100">
              Dashboard galerie
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">
              Gestion de la galerie
            </h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Consultez les images liées aux posts et accédez rapidement à leur édition.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-sm">
          <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row">
              <input
                type="text"
                placeholder="Rechercher par post, catégorie ou ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500 md:w-[500px]"
              />
              <button
                type="submit"
                className="rounded-xl bg-blue-500 px-4 py-3 font-bold text-white transition hover:bg-blue-600"
              >
                Rechercher
              </button>
              <button
                type="button"
                onClick={handleResetSearch}
                className="rounded-xl bg-slate-700 px-4 py-3 font-bold text-white transition hover:bg-slate-600"
              >
                Réinitialiser
              </button>
            </form>

            <button
              onClick={() => setShowAddModal(true)}
              className="rounded-xl bg-green-500 px-4 py-3 font-bold text-white transition hover:bg-green-600"
            >
              Ajouter une image
            </button>
          </div>

          {searchTerm && (
            <p className="mb-4 text-sm text-slate-400">
              Résultats pour{" "}
              <span className="font-semibold text-slate-200">"{searchTerm}"</span>
            </p>
          )}

          {error && (
            <div className="mb-6 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300">
              {error}
            </div>
          )}

          {galleryItems.length === 0 ? (
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-8 text-center text-slate-400 shadow">
              Aucune image dans la galerie.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {galleryItems.map((item) => {
                  const imageUrl = getImageUrl(item.thumbnail || item.path || item.picture);
                  return (
                    <div
                      key={item.id}
                      className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950 shadow-md"
                    >
                      <div className="h-64 w-full bg-slate-800">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={`Galerie ${item.id}`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center text-slate-500">
                            Aucune image
                          </div>
                        )}
                      </div>

                      <div className="p-4">
                        <h2 className="mb-2 text-lg font-semibold text-white">
                          Image #{item.id}
                        </h2>
                        <p className="mb-1 text-sm text-slate-400">
                          <span className="font-medium text-slate-200">Post lié :</span>{" "}
                          {item.postTitle || item.post?.title || `Post #${item.postId || item.post_id}`}
                        </p>
                        {item.category && (
                          <p className="mb-1 text-sm text-slate-400">
                            <span className="font-medium text-slate-200">Catégorie :</span>{" "}
                            {item.category}
                          </p>
                        )}
                        <div className="mt-4 flex gap-3">
                          {item.postSlug || item.post?.slug ? (
                            <Link
                              to={`/dashboard/posts/edit/${item.postSlug || item.post.slug}`}
                              className="flex-1 rounded bg-blue-500 px-4 py-2 text-center font-bold text-white transition hover:bg-blue-600"
                            >
                              Modifier le post
                            </Link>
                          ) : (
                            <div className="flex-1 rounded bg-slate-700 px-4 py-2 text-center font-semibold text-slate-400">
                              Aucun post
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="rounded-lg bg-orange-500 px-6 py-3 font-bold text-white transition hover:bg-orange-600 disabled:bg-orange-300"
                  >
                    {loadingMore ? "Chargement..." : "Charger plus"}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-xl">
            <h2 className="mb-4 text-2xl font-bold text-white">Ajouter une image</h2>
            <p className="mb-6 text-slate-400">
              Choisis comment tu veux ajouter une nouvelle photo dans la galerie.
            </p>
            <div className="flex flex-col gap-4">
              <Link
                to="/dashboard/posts"
                className="w-full rounded bg-blue-500 px-4 py-3 text-center font-bold text-white transition hover:bg-blue-600"
                onClick={() => setShowAddModal(false)}
              >
                Ajouter une photo à un post existant
              </Link>
              <Link
                to="/dashboard/posts/create"
                className="w-full rounded bg-green-500 px-4 py-3 text-center font-bold text-white transition hover:bg-green-600"
                onClick={() => setShowAddModal(false)}
              >
                Créer un nouveau post
              </Link>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="w-full rounded bg-slate-700 px-4 py-3 font-bold text-slate-200 transition hover:bg-slate-600"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryIndex;